import { useEffect, useState, type FormEvent } from "react";
import {
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Save,
} from "lucide-react";
import {
  api,
  ApiError,
  type LinkPreviewView,
  type Requirement,
} from "../api";
import { Button, Field, cx } from "../ui";

/**
 * Inline preview for a URL pasted into a comment thread (or any
 * other freeform text body). Server fetches the OpenGraph /
 * Twitter-card metadata; this component renders it.
 *
 * Design notes:
 *  - On any failure (errorCode set), we render a plain link instead
 *    of a card. A failed fetch is not interesting; the URL is.
 *  - Importable content types (PDF, png, jpg, webp, gif) get a
 *    "Save to project documents" action when the parent passes a
 *    non-empty importCandidates list. The action posts to the
 *    server's URL-import path, which re-validates the SSRF
 *    allowlist before pulling bytes.
 */
export function LinkPreviewCard({
  url,
  projectId,
  importCandidates,
  onImported,
}: {
  url: string;
  /** Required to render the import action. Without it the card is
   *  read-only metadata. */
  projectId?: string;
  /** Owner-upload-candidate document types for the project. The
   *  card renders the import button only when this is non-empty
   *  (owner is viewing this thread). */
  importCandidates?: Pick<
    Requirement,
    "documentTypeId" | "documentTypeCode" | "documentTypeName"
  >[];
  onImported?: () => void | Promise<void>;
}) {
  const [view, setView] = useState<LinkPreviewView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ data: LinkPreviewView }>(
        `/api/link-previews?url=${encodeURIComponent(url)}`,
      )
      .then((r) => {
        if (!cancelled) setView(r.data);
      })
      .catch(() => {
        if (!cancelled) setView(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (loading && !view) {
    return (
      <div className="mt-1.5 px-3 py-2.5 rounded-md border border-sand-200 bg-sand-50">
        <div className="text-[12px] text-ink-500 truncate">{url}</div>
        <div className="text-[10.5px] text-ink-400 mt-0.5">Loading preview…</div>
      </div>
    );
  }

  // Failed fetch (or plain non-HTML / blocked / unsupported): render
  // a plain link, not a card. The URL is still useful; the failed
  // metadata isn't.
  if (!view || view.errorCode) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[12.5px] text-brand-700 hover:text-brand-800 inline-flex items-center gap-1 mt-1 break-all"
      >
        <ExternalLink size={11} />
        {url}
      </a>
    );
  }

  const hostname = (() => {
    try {
      return new URL(view.url).hostname;
    } catch {
      return view.url;
    }
  })();

  const isImage =
    !!view.contentType && view.contentType.startsWith("image/");
  const isPdf = view.contentType === "application/pdf";

  return (
    <div className="mt-1.5 rounded-md border border-sand-200 bg-white overflow-hidden flex">
      {view.imageUrl ? (
        <div className="w-24 h-24 flex-shrink-0 bg-sand-100 overflow-hidden">
          <img
            src={view.imageUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      ) : isPdf ? (
        <div className="w-24 h-24 flex-shrink-0 bg-sand-100 inline-flex items-center justify-center">
          <FileText size={28} className="text-ink-400" />
        </div>
      ) : isImage ? (
        <div className="w-24 h-24 flex-shrink-0 bg-sand-100 inline-flex items-center justify-center">
          <ImageIcon size={28} className="text-ink-400" />
        </div>
      ) : null}
      <div className="flex-1 min-w-0 p-3">
        <a
          href={view.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[13px] font-semibold text-ink-800 hover:text-brand-800 no-underline hover:no-underline block truncate"
          title={view.title ?? hostname}
        >
          {view.title ?? hostname}
        </a>
        {view.description && (
          <p className="text-[12px] text-ink-600 leading-snug mt-0.5 line-clamp-2 m-0">
            {view.description}
          </p>
        )}
        <div className="text-[10.5px] text-ink-500 mt-1 inline-flex items-center gap-1">
          <ExternalLink size={10} />
          {view.siteName ?? hostname}
          {view.contentType && view.contentType !== "text/html" && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-sand-100 text-ink-600 text-[10px] uppercase tracking-wider">
              {labelForContentType(view.contentType)}
            </span>
          )}
        </div>
        {view.importable && projectId && importCandidates && importCandidates.length > 0 && (
          <ImportToProjectAction
            url={view.url}
            projectId={projectId}
            candidates={importCandidates}
            onDone={onImported}
          />
        )}
      </div>
    </div>
  );
}

function labelForContentType(ct: string): string {
  if (ct === "application/pdf") return "PDF";
  if (ct.startsWith("image/")) return ct.slice("image/".length);
  return ct;
}

function ImportToProjectAction({
  url,
  projectId,
  candidates,
  onDone,
}: {
  url: string;
  projectId: string;
  candidates: Pick<
    Requirement,
    "documentTypeId" | "documentTypeCode" | "documentTypeName"
  >[];
  onDone?: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(candidates[0]?.documentTypeCode ?? "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/projects/${projectId}/documents/from-url`, {
        url,
        documentTypeCode: code,
      });
      setDone(true);
      setOpen(false);
      await onDone?.();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(humanError(body?.error ?? "import_failed"));
      } else {
        setError("Import failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (done && !open) {
    return (
      <p className="text-[11.5px] text-status-approved-fg mt-2 m-0">
        Saved to project documents.
      </p>
    );
  }

  return (
    <div className="mt-2">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-[12px] text-brand-700 hover:text-brand-800 px-2 py-1 rounded-md hover:bg-brand-50"
        >
          <Save size={12} />
          Save to project documents
        </button>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-2">
          <Field
            label="Document type"
            hint="Pick which requirement this file fulfills."
          >
            <select
              className="select"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            >
              {candidates.map((c) => (
                <option key={c.documentTypeId} value={c.documentTypeCode}>
                  {c.documentTypeName}
                </option>
              ))}
            </select>
          </Field>
          {error && <div className="alert-error">{error}</div>}
          <div className="inline-flex items-center gap-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={busy}
              disabled={busy || !code}
            >
              <Save size={12} />
              Save
            </Button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              className={cx(
                "text-[12px] text-ink-500 hover:text-ink-700 px-2 py-1 rounded-md",
              )}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function humanError(code: string): string {
  switch (code) {
    case "blocked_internal_ip":
      return "That URL points to a private address. Pick a public link.";
    case "fetch_timeout":
      return "The remote server did not respond in time.";
    case "unreachable":
      return "Could not reach the remote server.";
    case "content_type_not_importable":
      return "Only PDF, PNG, JPG, WebP and GIF can be imported.";
    case "file_too_large":
      return "That file exceeds the 25 MB project document limit.";
    case "infected_quarantined":
      return "The file failed virus scan and was quarantined.";
    case "document_type_not_found":
      return "That document type isn't valid for this building.";
    case "forbidden":
      return "Only the unit owner can import a document into this project.";
    default:
      return "Import failed.";
  }
}
